import { useEffect, useRef, useState } from 'react';
import { Play, Pause, Loader2, FileText } from 'lucide-react';
import { api } from '@/contexts/AuthContext';

const BACKEND_URL = typeof window !== 'undefined' ? window.location.origin : process.env.REACT_APP_BACKEND_URL;

function fmtDuration(sec) {
  if (!sec || sec < 0) return '0:00';
  const s = Math.round(sec);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/**
 * VoiceBubble renders inline audio + waveform + transcribe button.
 *
 * `variant` (Feb 2026 fix):
 *   - "chat" (default) — white icons/text designed to sit INSIDE a colored
 *     chat bubble (Chat.jsx wraps this in a red/blue gradient bubble).
 *   - "profile" — dark icons/text with its own subtle red-tinted background.
 *     Use when rendering on white surfaces (Profile Edit, FullProfileBody
 *     preview cards). Without this, the chat-variant styles are white-on-
 *     white and the whole bubble looks broken/invisible.
 */
export function VoiceBubble({ message, matchId, isMe, variant = 'chat' }) {
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [loading, setLoading] = useState(false);
  const [transcript, setTranscript] = useState(message.transcript || '');
  const [txLoading, setTxLoading] = useState(false);
  const [txError, setTxError] = useState('');
  const audioRef = useRef(null);

  const bars = Array.isArray(message.waveform) && message.waveform.length > 0
    ? message.waveform
    : Array.from({ length: 32 }, () => 0.4 + Math.random() * 0.4);

  // Feb 2026 · voice playback fix (updated Feb 18 audit):
  //   Same-origin cookies flow with the <audio> request automatically, so we
  //   no longer append `?auth=<token>` (which leaked the session token into
  //   server logs + referrers). The backend still accepts cookie auth on
  //   `/api/files/*` — see `routes/profile.py::serve_file`.
  const src = message.local_src
    || (message.voice_url ? `${BACKEND_URL}${message.voice_url}` : '');

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    // Rewind + reload when the source flips (optimistic → server response).
    // Ignore empty src (initial render before upload starts).
    if (src) { try { audio.load(); } catch { /* older Safari can throw */ } }
    setProgress(0);
    setPlaying(false);
  }, [src]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onTime = () => {
      const dur = audio.duration || message.duration || 1;
      setProgress(Math.min(1, audio.currentTime / dur));
    };
    const onEnd = () => { setPlaying(false); setProgress(0); audio.currentTime = 0; };
    const onLoad = () => setLoading(false);
    audio.addEventListener('timeupdate', onTime);
    audio.addEventListener('ended', onEnd);
    audio.addEventListener('canplay', onLoad);
    return () => {
      audio.removeEventListener('timeupdate', onTime);
      audio.removeEventListener('ended', onEnd);
      audio.removeEventListener('canplay', onLoad);
    };
  }, [message.duration]);

  const toggle = async () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (playing) { audio.pause(); setPlaying(false); return; }
    setLoading(true);
    try {
      await audio.play();
      setPlaying(true);
    } catch {
      setLoading(false);
    }
  };

  const transcribe = async () => {
    if (transcript || txLoading) return;
    setTxLoading(true); setTxError('');
    try {
      const r = await api.post(`/chat/${matchId}/messages/${message.message_id}/transcribe`);
      setTranscript(r.data.transcript || '(no speech detected)');
    } catch (e) {
      setTxError(e.response?.data?.detail || 'Transcription failed');
    } finally {
      setTxLoading(false);
    }
  };

  const fillIdx = Math.floor(bars.length * progress);

  // Variant-scoped color tokens. `chat` variant assumes a colored parent
  // bubble and paints white. `profile` variant paints red/graphite on a
  // subtle red-tinted background so it stays legible on plain white
  // surfaces (Edit Profile, FullProfileBody preview).
  const isProfile = variant === 'profile';
  const wrapCls = isProfile
    ? 'flex flex-col gap-1.5 min-w-[220px] max-w-[300px] px-3 py-2 rounded-2xl bg-gradient-to-br from-[#FEF2F2] to-white border border-[#B91C1C]/15'
    : 'flex flex-col gap-1.5 min-w-[180px] max-w-[260px]';
  const iconCls = isProfile ? 'text-[#B91C1C]' : 'text-white';
  const playBtnCls = isProfile
    ? 'w-9 h-9 rounded-full flex-shrink-0 flex items-center justify-center transition-all active:scale-90 bg-[#B91C1C]/10 hover:bg-[#B91C1C]/20'
    : 'w-9 h-9 rounded-full flex-shrink-0 flex items-center justify-center transition-all active:scale-90 bg-white/25 hover:bg-white/35';
  const barPlayedCls = isProfile ? 'bg-[#B91C1C]' : 'bg-white';
  const barIdleCls = isProfile ? 'bg-[#B91C1C]/25' : 'bg-white/40';
  const durationCls = isProfile ? 'text-[#4B4B52]' : 'text-white/80';
  const transcribeCls = isProfile ? 'text-[#B91C1C] hover:text-[#B91C1C]/80' : 'text-white/80 hover:text-white';
  const transcriptCls = isProfile
    ? 'text-[12px] leading-snug italic border-l-2 pl-2 border-[#B91C1C]/40 text-[#4B4B52]'
    : 'text-[12px] leading-snug italic border-l-2 pl-2 border-white/50 text-white/90';
  const errCls = isProfile ? 'text-[10px] text-[#B45309]' : 'text-[10px] text-white/70';

  return (
    <div className={wrapCls}>
      <div className="flex items-center gap-2.5">
        <button
          onClick={toggle}
          disabled={loading}
          data-testid={`voice-play-${message.message_id || 'preview'}`}
          className={playBtnCls}
          aria-label={playing ? 'Pause' : 'Play'}
        >
          {loading
            ? <Loader2 size={14} className={`${iconCls} animate-spin`} />
            : playing
              ? <Pause size={14} className={iconCls} fill="currentColor" />
              : <Play size={14} className={`${iconCls} ml-0.5`} fill="currentColor" />}
        </button>

        {/* Waveform */}
        <div className="flex items-center gap-[2px] h-7 flex-1">
          {bars.map((b, i) => {
            const played = i < fillIdx;
            const h = Math.max(3, Math.round((b || 0.2) * 24));
            return (
              <span
                key={i}
                style={{ height: `${h}px` }}
                className={`w-[2px] rounded-full transition-colors ${played ? barPlayedCls : barIdleCls}`}
              />
            );
          })}
        </div>

        <span className={`text-[10px] font-semibold tabular-nums flex-shrink-0 ${durationCls}`}>
          {fmtDuration(message.duration)}
        </span>
      </div>

      {/* Transcribe CTA — only for chat messages with a real matchId + message_id. */}
      {matchId && message.message_id && !transcript && (
        <button
          onClick={transcribe}
          disabled={txLoading}
          data-testid={`voice-transcribe-${message.message_id}`}
          className={`self-start inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider ${transcribeCls}`}
        >
          {txLoading
            ? <><Loader2 size={10} className="animate-spin"/> Transcribing…</>
            : <><FileText size={10}/> Transcribe</>}
        </button>
      )}
      {txError && !transcript && (
        <span className={errCls}>{txError}</span>
      )}
      {transcript && (
        <div
          data-testid={`voice-transcript-${message.message_id || 'preview'}`}
          className={transcriptCls}
        >
          {transcript}
        </div>
      )}

      <audio ref={audioRef} src={src} preload="none" />
    </div>
  );
}

export default VoiceBubble;
